/**
 * Filter "tokens" — the unit the recommendation section relaxes and the unit whose
 * application order is kept in `searchPageData.filterApplicationOrder` (Redux only).
 *
 * A token is the filter's API key, e.g. `range_available_price`, or `key:value` for the
 * two multi-select filters that relax per option (`amenities:gym`, `bills_included:wifi`).
 * Pure module — no repo imports — so it stays unit-testable under the bare jest config.
 */

/** Standard (relaxable) filters, in search-panel order. Geo filters are never here. */
export const STANDARD_TOKEN_KEYS = [
  "range_available_price",
  "move_in_month",
  "leaseDuration",
  "meta_types",
  "guarantor",
  "available",
  "tags",
  "meta_unit_types",
  "amenities",
  "bills_included",
] as const;

export const MOVE_IN_TOKEN = "move_in_month";

/** A multi-select value as a list, from an array or a comma-separated string. */
export const asList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === "string") return value.split(",").filter(Boolean);
  return [];
};

export const tokenKey = (token: string) => token.split(":")[0];

export const splitToken = (token: string): [string, string | undefined] => {
  const idx = token.indexOf(":");
  if (idx === -1) return [token, undefined];
  return [token.slice(0, idx), token.slice(idx + 1)];
};

export const makeToken = (key: string, value?: string) =>
  value === undefined || value === "" ? key : `${key}:${String(value).toLowerCase()}`;

export const isStandardToken = (token: string) =>
  (STANDARD_TOKEN_KEYS as readonly string[]).includes(tokenKey(token));

export const panelPosition = (token: string) =>
  (STANDARD_TOKEN_KEYS as readonly string[]).indexOf(tokenKey(token));

/**
 * Merge the previous application order with the tokens now applied: surviving tokens keep
 * their slot, new tokens append in the order given (panel order). A re-added token is new.
 */
export const updateApplicationOrder = (prev: string[] = [], nextTokens: string[] = []) => {
  const kept = prev.filter((t) => nextTokens.includes(t));
  const added = nextTokens.filter((t) => !kept.includes(t));
  return [...kept, ...added];
};
